// ===========================================
// components/AdminPanel.js
// ===========================================
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, RefreshCw, Users, UserCheck, UserX, Book, Edit, Trash2, UserPlus, Eye, X } from 'lucide-react';
import { api } from '../utils/api';
import config from '../utils/config';

const AdminPanel = ({ onApproveUser, onRejectUser }) => {
  const API_BASE_URL = config.getApiUrl();
  
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [stats, setStats] = useState(null);
  
  // Book management state
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'books'
  const [books, setBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [booksPage, setBooksPage] = useState(1);
  const [booksTotal, setBooksTotal] = useState(0);
  const [booksSearch, setBooksSearch] = useState('');
  const [editingBook, setEditingBook] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningBook, setAssigningBook] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);

  // Fetch pending users from the database
  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPendingUsers();
      setPendingUsers(response.users || []);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setError(err.message || 'Failed to fetch pending users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin stats
  const fetchAdminStats = async () => {
    try {
      const response = await api.getAdminStats();
      setStats(response);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchPendingUsers();
    fetchAdminStats();
  }, []);

  // Fetch books for admin management
  const fetchBooks = async (page = 1, search = '') => {
    try {
      setBooksLoading(true);
      const response = await api.getAdminBooks({
        page,
        per_page: 10,
        search
      });
      setBooks(response.books || []);
      setBooksTotal(response.total || 0);
      setBooksPage(page);
    } catch (err) {
      console.error('Error fetching books:', err);
      setError(err.message || 'Failed to fetch books');
    } finally {
      setBooksLoading(false);
    }
  };

  // Fetch all users for assignment
  const fetchAllUsers = async () => {
    try {
      const response = await api.getAllUsers();
      setAllUsers(response.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Handle book editing
  const handleEditBook = async (bookData) => {
    try {
      await api.updateBook(editingBook.id, bookData);
      setShowEditModal(false);
      setEditingBook(null);
      fetchBooks(booksPage, booksSearch);
    } catch (err) {
      setError(err.message || 'Failed to update book');
    }
  };

  // Handle book deletion
  const handleDeleteBook = async (bookId) => {
    if (!window.confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return;
    }
    
    try {
      setActionLoading(prev => ({ ...prev, [bookId]: 'deleting' }));
      await api.deleteBook(bookId);
      fetchBooks(booksPage, booksSearch);
      fetchAdminStats(); // Update stats to reflect the new total books count
    } catch (err) {
      setError(err.message || 'Failed to delete book');
    } finally {
      setActionLoading(prev => ({ ...prev, [bookId]: null }));
    }
  };

  // Handle book assignment
  const handleAssignBook = async () => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }

    try {
      await api.assignBookToUsers(assigningBook.id, selectedUsers);
      setShowAssignModal(false);
      setAssigningBook(null);
      setSelectedUsers([]);
    } catch (err) {
      setError(err.message || 'Failed to assign book');
    }
  };

  // Fetch book assignments
  const fetchBookAssignments = async (bookId) => {
    try {
      const response = await api.getBookAssignments(bookId);
      setAssignments(response.assignments || []);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  };

  // Remove assignment
  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await api.removeBookAssignment(assignmentId);
      fetchBookAssignments(assigningBook.id);
    } catch (err) {
      setError(err.message || 'Failed to remove assignment');
    }
  };

  // Load books when switching to books tab
  useEffect(() => {
    if (activeTab === 'books') {
      fetchBooks();
      fetchAllUsers();
    }
  }, [activeTab]);

  // Handle search with debounce
  useEffect(() => {
    if (activeTab === 'books') {
      const timer = setTimeout(() => {
        fetchBooks(1, booksSearch);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [booksSearch, activeTab]);

  // Handle user approval
  const handleApproveUser = async (userId) => {
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'approving' }));
      await api.approveUser(userId);
      
      // Update local state
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      
      // Call parent callback if provided
      if (onApproveUser) {
        onApproveUser(userId);
      }
    } catch (err) {
      console.error('Error approving user:', err);
      setError(err.message || 'Failed to approve user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  // Handle user rejection
  const handleRejectUser = async (userId) => {
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'rejecting' }));
      await api.rejectUser(userId);
      
      // Update local state
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      
      // Call parent callback if provided
      if (onRejectUser) {
        onRejectUser(userId);
      }
    } catch (err) {
      console.error('Error rejecting user:', err);
      setError(err.message || 'Failed to reject user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Panel</h2>
        <button
          onClick={() => {
            fetchPendingUsers();
            fetchAdminStats();
            if (activeTab === 'books') {
              fetchBooks(booksPage, booksSearch);
            }
          }}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">User </span>Management
          </button>
          <button
            onClick={() => setActiveTab('books')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'books'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Book className="h-4 w-4 inline mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Book </span>Management
          </button>
        </nav>
      </div>

            {/* Admin Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Approved</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.approved_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center">
              <UserX className="h-8 w-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.pending_users}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex items-center">
              <div className="h-8 w-8 text-purple-600 flex items-center justify-center font-bold text-lg">📚</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Books</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total_books}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Pending User Approvals
              {!loading && pendingUsers.length > 0 && (
                <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                  {pendingUsers.length}
                </span>
              )}
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="px-6 py-8 text-center">
                <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                <p className="text-gray-500">Loading pending users...</p>
              </div>
            ) : pendingUsers.length > 0 ? (
              pendingUsers.map((pendingUser) => (
                <div key={pendingUser.id} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {pendingUser.first_name} {pendingUser.last_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      @{pendingUser.username || 'No username'}
                    </p>
                    <p className="text-xs text-gray-400">
                      Requested: {formatDate(pendingUser.created_at)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Telegram ID: {pendingUser.telegram_id}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApproveUser(pendingUser.id)}
                      disabled={actionLoading[pendingUser.id]}
                      className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                    >
                      {actionLoading[pendingUser.id] === 'approving' ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectUser(pendingUser.id)}
                      disabled={actionLoading[pendingUser.id]}
                      className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                    >
                      {actionLoading[pendingUser.id] === 'rejecting' ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      Reject
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No pending user approvals.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'books' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h3 className="text-lg font-medium text-gray-900">Book Management</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search books..."
                  value={booksSearch}
                  onChange={(e) => setBooksSearch(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {booksLoading ? (
              <div className="px-6 py-8 text-center">
                <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                <p className="text-gray-500">Loading books...</p>
              </div>
            ) : books.length > 0 ? (
              books.map((book) => (
                <div key={book.id} className="px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <img
                          src={book.thumbnail_url 
                            ? (book.thumbnail_url.startsWith('http') 
                                ? `${API_BASE_URL.replace('/api', '')}/uploads/thumbnails/${book.thumbnail_url.split('/').pop()}`
                                : `${API_BASE_URL.replace('/api', '')}${book.thumbnail_url}`)
                            : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDIwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjUwIiBmaWxsPSIjNjM2NmYxIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTI1IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj7wn5OCIE5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K'
                          }
                          alt={book.title}
                          className="w-16 h-20 object-cover rounded shadow"
                          onLoad={(e) => {
                            console.log('✅ Thumbnail loaded successfully via proxy');
                          }}
                          onError={(e) => {
                            console.log('❌ Thumbnail failed to load via proxy');
                            // Replace with a simple placeholder
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDIwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjUwIiBmaWxsPSIjNjM2NmYxIi8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTI1IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj7wn5OCIE5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                          }}
                        />
                        <div className="flex-1">
                          <h4 className="text-lg font-medium text-gray-900">{book.title}</h4>
                          {book.author && (
                            <p className="text-sm text-gray-600">by {book.author}</p>
                          )}
                          <div className="text-xs text-gray-500 mt-1 space-y-1">
                            <p>Size: {formatFileSize(book.file_size)}</p>
                            <p>Uploaded: {formatDate(book.upload_date)}</p>
                            <p>By: {book.uploader_name}</p>
                            <p>Downloads: {book.download_count}</p>
                            <p>Status: <span className={`font-medium ${book.is_active ? 'text-green-600' : 'text-red-600'}`}>
                              {book.is_active ? 'Active' : 'Inactive'}
                            </span></p>
                          </div>
                          {book.description && (
                            <p className="text-sm text-gray-700 mt-2">{book.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-col sm:w-auto">
                      <button
                        onClick={() => {
                          setEditingBook(book);
                          setShowEditModal(true);
                        }}
                        className="bg-blue-600 text-white px-2 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 text-sm"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => {
                          setAssigningBook(book);
                          setShowAssignModal(true);
                        }}
                        className="bg-green-600 text-white px-2 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-1 text-sm"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span className="hidden sm:inline">Assign</span>
                      </button>
                      <button
                        onClick={() => {
                          setAssigningBook(book);
                          fetchBookAssignments(book.id);
                          setShowAssignmentsModal(true);
                        }}
                        className="bg-purple-600 text-white px-2 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-1 text-sm"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden sm:inline">View</span>
                      </button>
                      <button
                        onClick={() => handleDeleteBook(book.id)}
                        disabled={actionLoading[book.id]}
                        className="bg-red-600 text-white px-2 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1 text-sm"
                      >
                        {actionLoading[book.id] === 'deleting' ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <Book className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No books found.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {booksTotal > 10 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-500 text-center sm:text-left">
                Showing {books.length} of {booksTotal} books
              </p>
              <div className="flex gap-2 justify-center sm:justify-end">
                <button
                  onClick={() => fetchBooks(booksPage - 1, booksSearch)}
                  disabled={booksPage <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 text-sm"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-500">
                  Page {booksPage}
                </span>
                <button
                  onClick={() => fetchBooks(booksPage + 1, booksSearch)}
                  disabled={booksPage * 10 >= booksTotal}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 text-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Book Modal */}
      {showEditModal && editingBook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Edit Book</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <EditBookForm
              book={editingBook}
              onSave={handleEditBook}
              onCancel={() => setShowEditModal(false)}
            />
          </div>
        </div>
      )}

      {/* Assign Book Modal */}
      {showAssignModal && assigningBook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm sm:text-lg font-medium">Assign Book: {assigningBook.title}</h3>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <AssignBookForm
              users={allUsers}
              selectedUsers={selectedUsers}
              onSelectionChange={setSelectedUsers}
              onAssign={handleAssignBook}
              onCancel={() => setShowAssignModal(false)}
            />
          </div>
        </div>
      )}

      {/* View Assignments Modal */}
      {showAssignmentsModal && assigningBook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-lg max-h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm sm:text-lg font-medium">Assignments: {assigningBook.title}</h3>
              <button
                onClick={() => setShowAssignmentsModal(false)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <ViewAssignmentsModal
              assignments={assignments}
              onRemoveAssignment={handleRemoveAssignment}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const EditBookForm = ({ book, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: book.title || '',
    author: book.author || '',
    description: book.description || '',
    is_active: book.is_active
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Author
        </label>
        <input
          type="text"
          value={formData.author}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
          Active
        </label>
      </div>
      <div className="flex gap-2 pt-4">
        <button
          type="submit"
          className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Save Changes
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const AssignBookForm = ({ users, selectedUsers, onSelectionChange, onAssign, onCancel }) => {
  const handleUserToggle = (userId) => {
    const updatedSelection = selectedUsers.includes(userId)
      ? selectedUsers.filter(id => id !== userId)
      : [...selectedUsers, userId];
    onSelectionChange(updatedSelection);
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-3">Select users to assign this book to:</p>
        <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
          {users.map((user) => (
            <div key={user.id} className="px-3 py-2 border-b border-gray-100 last:border-b-0">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => handleUserToggle(user.id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">
                  {user.full_name} {user.username && `(@${user.username})`}
                  {user.is_admin && <span className="ml-1 text-xs text-blue-600">(Admin)</span>}
                </span>
              </label>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
        </p>
      </div>
      <div className="flex gap-2 pt-4">
        <button
          onClick={onAssign}
          disabled={selectedUsers.length === 0}
          className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          Assign to {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ViewAssignmentsModal = ({ assignments, onRemoveAssignment }) => {
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-4">
      {assignments.length > 0 ? (
        <div className="max-h-60 overflow-y-auto">
          {assignments.map((assignment) => (
            <div key={assignment.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg mb-2">
              <div>
                <p className="text-sm font-medium text-gray-900">{assignment.user_name}</p>
                {assignment.username && (
                  <p className="text-xs text-gray-500">@{assignment.username}</p>
                )}
                <p className="text-xs text-gray-400">
                  Assigned: {formatDate(assignment.assigned_at)}
                </p>
                <p className="text-xs text-gray-400">
                  By: {assignment.assigned_by}
                </p>
              </div>
              <button
                onClick={() => onRemoveAssignment(assignment.id)}
                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors text-sm"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No assignments found for this book.</p>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;